import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { Button, Spinner } from "flowbite-react";
import { MdAdd, MdDelete } from "react-icons/md";
import moment from "moment";

import ImageInput from "../inputs/ImageInput";
import {
  MdOutlineBadge,
  MdOutlineWorkspacePremium,
  MdOutlineBolt,
  MdOutlineSchedule,
  MdOutlineLanguage,
  MdOutlinePayments,
} from "react-icons/md";
import FormTabs from "./FormTabs";
import FormFooter from "./FormFooter";
import Input from "../inputs/Input";
import PhoneInput from "../inputs/PhoneInput";
import SelectInput from "../inputs/SelectInput";
import TextAreaInput from "../inputs/TextAreaInput";
import { AVATAR, CLIENTS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { formatCurrencyBRL } from "../../utils/utils";
import {
  describeExpiration,
} from "../customers/customerHelpers";

const WEEKDAY_LABELS = [
  "Domingo",
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
];

const EXTEND_PRESETS = [7, 15, 30, 90];

const MAX_CARD_MACHINES = 5;
const MAX_CREDIT_INSTALLMENTS = 24;

const FEE_BEARER_OPTIONS = [
  { value: "ESTABELECIMENTO", label: "Estabelecimento" },
  { value: "PROFISSIONAL", label: "Profissional" },
];

const emptyMachine = () => ({
  nickname: "",
  taxPercentage: 0,
  maxInstallments: 1,
  installmentRates: [0],
  feeBearer: "ESTABELECIMENTO",
  feeBearerProfessionalIds: [],
});

/** Redimensiona a lista de taxas quando o nº de parcelas muda. */
const buildInstallmentRates = (maxInstallments, currentRates = []) =>
  Array.from({ length: maxInstallments }, (_, index) => {
    const current = currentRates[index];

    return current === undefined || current === null || current === ""
      ? currentRates[0] ?? 0
      : current;
  });

const COLOR_THEMES = [
  { value: "red", label: "Vermelho" },
  { value: "pink", label: "Rosa" },
  { value: "purple", label: "Roxo" },
  { value: "blue", label: "Azul" },
  { value: "green", label: "Verde" },
  { value: "orange", label: "Laranja" },
  { value: "black", label: "Preto" },
];

const planSummaries = {
  FREE: { label: "Free", price: 0, tagline: "Plano introdutório" },
  ESSENTIAL: {
    label: "Essential",
    price: 19.9,
    tagline: "Recursos extras para agendamento em crescimento",
  },
  PRO: {
    label: "Pro",
    price: 29.9,
    tagline: "Controle completo com automações",
  },
};

const CREATE_TABS = [
  { key: "data", label: "Dados", icon: MdOutlineBadge },
  { key: "plan", label: "Plano", icon: MdOutlineWorkspacePremium },
  { key: "automation", label: "Automação", icon: MdOutlineBolt },
];

const EDIT_TABS = [
  { key: "data", label: "Dados", icon: MdOutlineBadge },
  { key: "plan", label: "Plano", icon: MdOutlineWorkspacePremium },
  { key: "automation", label: "Automação", icon: MdOutlineBolt },
  { key: "hours", label: "Horários", icon: MdOutlineSchedule },
  { key: "site", label: "Site", icon: MdOutlineLanguage },
  { key: "payments", label: "Pagamentos", icon: MdOutlinePayments },
];

/*
  Em qual aba mora cada campo obrigatório.

  Serve para a aba acender quando a validação reprova um campo que está fora
  da vista: num formulário de seis abas, clicar em "Salvar" e nada acontecer
  porque falta o e-mail — que está na primeira aba enquanto você está na de
  Horários — é indistinguível de um botão quebrado.
*/
const TAB_BY_FIELD = {
  name: "data",
  email: "data",
  phoneNumber: "data",
  documentId: "data",
  planId: "plan",
  numberOfUsers: "plan",
  planExpirationDate: "plan",
};

const buildCreateDefaults = () => ({
  name: "",
  phoneNumber: "",
  email: "",
  documentType: "CPF",
  documentId: "",
  planExpirationDate: moment().add(30, "days").format("YYYY-MM-DD"),
  numberOfUsers: 1,
  planId: "FREE",
  customer_id: "",
  cupom: "",
  isActiveAutomaticReminder: false,
  hourAutomaticReminder: 20,
});

const SectionTitle = ({ title, hint }) => (
  <div className="mb-4">
    <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
    {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
  </div>
);

const Toggle = ({ name, register, label, hint }) => (
  <label className="flex cursor-pointer items-start gap-3">
    <input
      type="checkbox"
      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-pink-600 focus:ring-pink-500"
      {...register(name)}
    />
    <span>
      <span className="block text-sm text-slate-800">{label}</span>
      {hint ? <span className="block text-xs text-slate-500">{hint}</span> : null}
    </span>
  </label>
);

/**
 * Lista de máquinas de um tipo de cartão. No crédito abre a grade de taxas
 * por parcela; no débito fica só a taxa única.
 */
const MachineList = ({
  title,
  hint,
  machines,
  professionals,
  supportsInstallments = false,
  onAdd,
  onRemove,
  onChange,
  onInstallmentRateChange,
  onToggleProfessional,
}) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5">
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        <p className="text-xs text-slate-500">{hint}</p>
      </div>
      <Button
        size="xs"
        color="light"
        onClick={onAdd}
        disabled={machines.length >= MAX_CARD_MACHINES}
        title={
          machines.length >= MAX_CARD_MACHINES
            ? `Limite de ${MAX_CARD_MACHINES} máquinas`
            : "Adicionar máquina"
        }
      >
        <MdAdd className="mr-1 h-4 w-4" />
        Adicionar
      </Button>
    </div>

    {machines.length ? (
      <div className="space-y-3">
        {machines.map((machine, index) => (
          <div
            key={`${title}-${index}`}
            className="rounded-xl border border-slate-100 bg-slate-50 p-4"
          >
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[180px] flex-1">
                <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Apelido da máquina
                </label>
                <input
                  type="text"
                  placeholder="Ex.: Stone, PagSeguro"
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white text-slate-900 transition placeholder:text-slate-400 focus:border-pink-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 px-3 py-1.5 text-sm"
                  value={machine.nickname}
                  onChange={(event) =>
                    onChange(index, "nickname", event.target.value)
                  }
                />
              </div>

              {supportsInstallments ? (
                <div className="w-32">
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Máx. parcelas
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={MAX_CREDIT_INSTALLMENTS}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white text-slate-900 transition placeholder:text-slate-400 focus:border-pink-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 px-3 py-1.5 text-sm"
                    value={machine.maxInstallments}
                    onChange={(event) =>
                      onChange(index, "maxInstallments", event.target.value)
                    }
                  />
                </div>
              ) : (
                <div className="w-32">
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Taxa (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white text-slate-900 transition placeholder:text-slate-400 focus:border-pink-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 px-3 py-1.5 text-right text-sm"
                    value={machine.taxPercentage}
                    onChange={(event) =>
                      onChange(index, "taxPercentage", event.target.value)
                    }
                  />
                </div>
              )}

              <div className="w-48">
                <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Quem paga a taxa
                </label>
                <select
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white text-slate-900 transition placeholder:text-slate-400 focus:border-pink-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 px-3 py-1.5 text-sm"
                  value={machine.feeBearer || "ESTABELECIMENTO"}
                  onChange={(event) =>
                    onChange(index, "feeBearer", event.target.value)
                  }
                >
                  {FEE_BEARER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <Button
                size="xs"
                color="light"
                onClick={() => onRemove(index)}
                title="Remover máquina"
              >
                <MdDelete className="text-rose-500" />
              </Button>
            </div>

            {machine.feeBearer === "PROFISSIONAL" ? (
              <div className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Profissionais que absorvem a taxa
                </p>
                {professionals.length ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {professionals.map((professional) => {
                      const isSelected = (
                        machine.feeBearerProfessionalIds || []
                      ).includes(professional.id);

                      return (
                        <button
                          key={professional.id}
                          type="button"
                          onClick={() =>
                            onToggleProfessional(index, professional.id)
                          }
                          className={`rounded-full border px-3 py-1 text-xs transition ${
                            isSelected
                              ? "border-pink-500 bg-pink-50 text-pink-600"
                              : "border-slate-200 text-slate-500 hover:text-slate-800"
                          }`}
                        >
                          {professional.name}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-slate-500">
                    Nenhum profissional cadastrado.
                  </p>
                )}
              </div>
            ) : null}

            {supportsInstallments ? (
              <div className="mt-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Taxa por parcela (%)
                </p>
                <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                  {(machine.installmentRates || []).map((rate, rateIndex) => (
                    <div key={rateIndex} className="flex items-center gap-1">
                      <span className="w-8 shrink-0 text-xs text-slate-500">
                        {rateIndex + 1}x
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        className="w-full rounded-lg border border-slate-200 bg-white text-slate-900 transition placeholder:text-slate-400 focus:border-pink-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 px-2 py-1 text-right text-sm"
                        value={rate}
                        onChange={(event) =>
                          onInstallmentRateChange(
                            index,
                            rateIndex,
                            event.target.value,
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    ) : (
      <p className="text-sm text-slate-500">
        Nenhuma máquina cadastrada. Sem máquina, o método fica sem taxa.
      </p>
    )}
  </div>
);

/**
 * Formulário de suporte do cliente. Em EDIT carrega tudo que o back-office
 * pode alterar (dados, plano, adicionais, automação, horários, site e taxas)
 * e salva em uma única chamada; em CREATE mantém o cadastro enxuto.
 */
const CompanySupportForm = ({
  company,
  fetchCompanies,
  isDrawerOpen,
  onClose,
  owners = [],
  canAssignOwner = false,
  type = "CREATE",
}) => {
  const isEdit = type === "EDIT";
  const tabs = isEdit ? EDIT_TABS : CREATE_TABS;

  const [activeTab, setActiveTab] = useState("data");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [workingHours, setWorkingHours] = useState([]);
  const [payments, setPayments] = useState([]);
  const [creditMachines, setCreditMachines] = useState([]);
  const [debitMachines, setDebitMachines] = useState([]);
  const [pendingExtendDays, setPendingExtendDays] = useState(0);
  const [avatarFile, setAvatarFile] = useState("");
  const [avatarPreview, setAvatarPreview] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, dirtyFields },
  } = useForm({ defaultValues: buildCreateDefaults() });

  // Estado carregado do servidor, para enviar só as seções que mudaram.
  const initialStateRef = useRef({
    workingHours: [],
    payments: [],
    creditMachines: [],
    debitMachines: [],
  });

  const { data: editableData, isLoading: isLoadingEditableData } = useQuery(
    ["company-editable", company?.id],
    async () => {
      const { data: response } = await axios.get(
        `${CLIENTS.FULL}/${company.id}/full`
      );

      return response.data;
    },
    { enabled: isEdit && Boolean(company?.id) && Boolean(isDrawerOpen) }
  );

  useEffect(() => {
    setActiveTab("data");
    setError(null);
    setPendingExtendDays(0);
  }, [company?.id, isDrawerOpen, type]);

  useEffect(() => {
    if (!isEdit) {
      reset(buildCreateDefaults());
      setWorkingHours([]);
      setPayments([]);
      setCreditMachines([]);
      setDebitMachines([]);
      return;
    }

    if (!editableData) return;

    const record = editableData.company;

    reset({
      name: record.name || "",
      email: record.email || "",
      phoneNumber: record.phoneNumber || "",
      documentType: record.documentType || "CPF",
      documentId: record.documentId || "",
      planId: record.planId || "FREE",
      planExpirationDate: record.planExpirationDate
        ? moment(record.planExpirationDate).format("YYYY-MM-DD")
        : "",
      numberOfUsers: record.numberOfUsers ?? 1,
      customer_id: record.customer_id || "",
      cupom: record.cupom || "",
      isActiveAutomaticReminder: Boolean(record.isActiveAutomaticReminder),
      hourAutomaticReminder: record.hourAutomaticReminder ?? 20,
      ownerUserId: record.ownerUserId ?? "",
      site: {
        isVisibleWebsite: Boolean(editableData.site?.isVisibleWebsite),
        isOnlineCalendar: Boolean(editableData.site?.isOnlineCalendar),
        urlSite: editableData.site?.urlSite || "",
        address: editableData.site?.address || "",
        description: editableData.site?.description || "",
        colorTheme: editableData.site?.colorTheme || "red",
        whatsapp: editableData.site?.whatsapp || "",
        instagram: editableData.site?.instagram || "",
        facebook: editableData.site?.facebook || "",
        tiktok: editableData.site?.tiktok || "",
        twitter: editableData.site?.twitter || "",
      },
    });

    setWorkingHours(
      (editableData.workingHours || []).map((item) => ({
        ...item,
        startHour: String(item.startHour || "07:00").slice(0, 5),
        endHour: String(item.endHour || "22:00").slice(0, 5),
        isOpen: Boolean(item.isOpen),
      }))
    );
    setPayments(editableData.payments || []);
    const loadedWorkingHours = (editableData.workingHours || []).map((item) => ({
      ...item,
      startHour: String(item.startHour || "07:00").slice(0, 5),
      endHour: String(item.endHour || "22:00").slice(0, 5),
      isOpen: Boolean(item.isOpen),
    }));

    setCreditMachines(editableData.cardMachines?.creditMachines || []);
    setDebitMachines(editableData.cardMachines?.debitMachines || []);

    initialStateRef.current = {
      workingHours: loadedWorkingHours,
      payments: editableData.payments || [],
      creditMachines: editableData.cardMachines?.creditMachines || [],
      debitMachines: editableData.cardMachines?.debitMachines || [],
    };
    setAvatarPreview(record.imageUrl || "");
    setAvatarFile("");
  }, [editableData, isEdit, reset]);

  const watchedPlan = watch("planId") || "FREE";
  const watchedExpiration = watch("planExpirationDate");
  const selectedPlan = planSummaries[watchedPlan] ?? planSummaries.FREE;

  const expirationPreview = useMemo(() => {
    if (!watchedExpiration) return null;

    const base = moment(watchedExpiration, "YYYY-MM-DD");
    const target = pendingExtendDays
      ? moment.max(base, moment()).clone().add(pendingExtendDays, "days")
      : base;

    return {
      date: target,
      days: target.startOf("day").diff(moment().startOf("day"), "days"),
    };
  }, [watchedExpiration, pendingExtendDays]);

  const handleWorkingHourChange = (weekday, field, value) => {
    setWorkingHours((previous) =>
      previous.map((item) =>
        item.weekday === weekday ? { ...item, [field]: value } : item
      )
    );
  };

  const updateMachine = (setMachines) => (index, field, value) => {
    setMachines((previous) =>
      previous.map((machine, machineIndex) => {
        if (machineIndex !== index) return machine;

        if (field === "maxInstallments") {
          const max = Math.min(
            Math.max(parseInt(value, 10) || 1, 1),
            MAX_CREDIT_INSTALLMENTS
          );

          return {
            ...machine,
            maxInstallments: max,
            installmentRates: buildInstallmentRates(
              max,
              machine.installmentRates
            ),
          };
        }

        return { ...machine, [field]: value };
      })
    );
  };

  const updateCreditMachine = updateMachine(setCreditMachines);
  const updateDebitMachine = updateMachine(setDebitMachines);

  const handleInstallmentRateChange = (index, installmentIndex, value) => {
    setCreditMachines((previous) =>
      previous.map((machine, machineIndex) =>
        machineIndex === index
          ? {
              ...machine,
              installmentRates: machine.installmentRates.map((rate, rateIndex) =>
                rateIndex === installmentIndex ? value : rate
              ),
            }
          : machine
      )
    );
  };

  const toggleFeeBearerProfessional = (setMachines) => (index, professionalId) => {
    setMachines((previous) =>
      previous.map((machine, machineIndex) => {
        if (machineIndex !== index) return machine;

        const current = machine.feeBearerProfessionalIds || [];

        return {
          ...machine,
          feeBearerProfessionalIds: current.includes(professionalId)
            ? current.filter((id) => id !== professionalId)
            : [...current, professionalId],
        };
      })
    );
  };

  const handlePaymentTaxChange = (paymentId, value) => {
    setPayments((previous) =>
      previous.map((payment) =>
        payment.id === paymentId ? { ...payment, taxPercentage: value } : payment
      )
    );
  };

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (!isEdit) {
        await axios.post(CLIENTS.CREATE, data);
        reset(buildCreateDefaults());
      } else {
        const { site, ...companyFields } = data;

        // Só sobe imagem nova quando o preview virou um data URL.
        if (avatarPreview && avatarPreview.includes("data:image/")) {
          const formData = new FormData();
          formData.append("image", avatarFile);

          const { data: uploaded } = await axios.post(
            `${AVATAR.UPLOAD}?avatar=${editableData?.company?.avatar || ""}`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );

          companyFields.avatar = uploaded.imageName;
        }

        const initial = initialStateRef.current;
        const hasChanged = (current, original) =>
          JSON.stringify(current) !== JSON.stringify(original);

        const payload = {
          company: companyFields,
          extendDays: pendingExtendDays || undefined,
        };

        // Enviar uma seção intocada só cria risco (o urlSite, por exemplo,
        // tem índice único no banco), então cada aba só vai se mudou.
        if (dirtyFields.site) {
          payload.site = site;
        }

        if (hasChanged(workingHours, initial.workingHours)) {
          payload.workingHours = workingHours;
        }

        if (hasChanged(payments, initial.payments)) {
          payload.payments = payments.map((payment) => ({
            id: payment.id,
            taxPercentage: Number(payment.taxPercentage) || 0,
          }));
        }

        if (
          hasChanged(creditMachines, initial.creditMachines) ||
          hasChanged(debitMachines, initial.debitMachines)
        ) {
          payload.cardMachines = {
            creditMachines: creditMachines.map((machine) => ({
              ...machine,
              taxPercentage: Number(machine.installmentRates?.[0] || 0),
              maxInstallments: Number(machine.maxInstallments || 1),
              installmentRates: (machine.installmentRates || []).map(
                (rate) => Number(rate) || 0
              ),
            })),
            debitMachines: debitMachines.map((machine) => ({
              ...machine,
              taxPercentage: Number(machine.taxPercentage) || 0,
            })),
          };
        }

        await axios.put(
          `${CLIENTS.FULL}/${company.id}/full`,
          payload
        );
      }

      onClose(false);
      fetchCompanies();
    } catch (submitError) {
      setError(submitError);
      console.error(submitError);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEdit && isLoadingEditableData) {
    return (
      <div className="flex items-center justify-center gap-3 py-16 text-sm text-slate-500">
        <Spinner size="md" />
        Carregando dados do cliente…
      </div>
    );
  }

  const professionals = editableData?.professionals || [];

  // Quais abas têm campo obrigatório pendente agora.
  const errorsByTab = Object.keys(errors || {}).reduce((acc, campo) => {
    const aba = TAB_BY_FIELD[campo];
    if (aba) acc[aba] = true;
    return acc;
  }, {});

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
      {/*
        O cartão de identidade que havia aqui saiu: repetia o cabeçalho do
        modal e, quando o formulário era aberto pela ficha do cliente, exibia
        "Sem data" — recebia um objeto reduzido, sem `daysToExpire`.
      */}
      <FormTabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
        errorsByTab={errorsByTab}
      />

      {activeTab === "data" ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <SectionTitle
            title="Dados cadastrais"
            hint="Identificação e contato principal da empresa."
          />
          {isEdit ? (
            <div className="mb-5">
              <ImageInput
                image={avatarFile}
                setImage={setAvatarFile}
                previewUrl={avatarPreview}
                setPreviewUrl={setAvatarPreview}
              />
            </div>
          ) : null}
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              type="text"
              label="Nome *"
              name="name"
              required
              register={register}
              error={errors?.name && "Este campo é obrigatório"}
            />
            {/*
              `value` explícito aqui por causa do react-input-mask.

              O InputMask guarda o próprio valor e não relê o ref depois que o
              `reset()` do react-hook-form popula o formulário — então o
              telefone JÁ CARREGADO aparecia como campo vazio num cadastro que
              o tem. O dado nunca se perdia (o submit enviava o valor certo do
              store), mas um campo obrigatório em branco faz qualquer um
              redigitar — e redigitar telefone é como se erra telefone.

              Passar `value` junto com o `onChange` que vem do `register`
              mantém o campo editável: digitar atualiza o store, o `watch`
              devolve o valor novo e o componente re-renderiza.
            */}
            <PhoneInput
              label="Telefone *"
              name="phoneNumber"
              required
              register={register}
              value={watch("phoneNumber") ?? ""}
              error={errors?.phoneNumber && "Este campo é obrigatório"}
            />
            <Input
              type="email"
              label="E-mail *"
              name="email"
              required
              register={register}
              error={errors?.email && "Informe um e-mail válido"}
            />
            <SelectInput
              label="Tipo de documento"
              name="documentType"
              register={register}
            >
              <option value="CPF">CPF</option>
              <option value="CNPJ">CNPJ</option>
            </SelectInput>
            <Input
              type="text"
              label="Documento"
              name="documentId"
              register={register}
            />
            {isEdit && canAssignOwner ? (
              <SelectInput
                label="Responsável pela conta"
                name="ownerUserId"
                register={register}
              >
                <option value="">Sem responsável</option>
                {owners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name} ({owner.clientCount} clientes)
                  </option>
                ))}
              </SelectInput>
            ) : null}
          </div>
        </section>
      ) : null}

      {activeTab === "plan" ? (
        <section className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <SectionTitle
              title="Plano e capacidade"
              hint="Define o que o cliente pode usar e até quando."
            />
            <div className="grid gap-4 md:grid-cols-3">
              <SelectInput label="Plano *" name="planId" required register={register}>
                <option value="FREE">Free</option>
                <option value="ESSENTIAL">Essential</option>
                <option value="PRO">Pro</option>
              </SelectInput>
              <Input
                type="number"
                label="Número de usuários *"
                name="numberOfUsers"
                min="1"
                required
                register={register}
                error={errors?.numberOfUsers && "Informe a quantidade"}
              />
              <Input
                type="date"
                label="Expiração do plano"
                name="planExpirationDate"
                register={register}
              />
            </div>

            {isEdit ? (
              <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-3">
                <span className="text-xs uppercase tracking-[0.2em] text-slate-400">
                  Estender
                </span>
                {EXTEND_PRESETS.map((days) => (
                  <Button
                    key={days}
                    size="xs"
                    color={pendingExtendDays === days ? "dark" : "light"}
                    onClick={() =>
                      setPendingExtendDays((previous) =>
                        previous === days ? 0 : days
                      )
                    }
                  >
                    +{days}d
                  </Button>
                ))}
                {expirationPreview ? (
                  <span className="text-xs text-slate-600">
                    {pendingExtendDays
                      ? `Ao salvar, expira em ${expirationPreview.date.format(
                          "DD/MM/YYYY"
                        )} (${expirationPreview.days} dias)`
                      : describeExpiration(expirationPreview.days)}
                  </span>
                ) : null}
              </div>
            ) : null}

            <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                {selectedPlan.label} · {selectedPlan.tagline}
              </p>
              <p className="text-2xl font-bold text-slate-900">
                {formatCurrencyBRL.format(selectedPlan.price)}
              </p>
              <p className="text-xs text-slate-500">Valor mensal estimado</p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <SectionTitle
              title="Cobrança"
              hint="Vínculo com o gateway e cupom aplicado na assinatura."
            />
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                type="text"
                label="ID do cliente no Asaas"
                name="customer_id"
                register={register}
              />
              <Input type="text" label="Cupom" name="cupom" register={register} />
            </div>
          </div>
        </section>
      ) : null}

      {activeTab === "automation" ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <SectionTitle
            title="Lembretes automáticos"
            hint="Disparo diário de lembretes de agendamento para os clientes do salão."
          />
          <div className="space-y-4">
            <Toggle
              name="isActiveAutomaticReminder"
              register={register}
              label="Enviar lembretes automáticos"
              hint="Quando desligado, nenhum lembrete é disparado para esta empresa."
            />
            <div className="md:w-1/2">
              <Input
                type="number"
                label="Hora do disparo (0-23)"
                name="hourAutomaticReminder"
                min="0"
                max="23"
                register={register}
              />
            </div>
          </div>
        </section>
      ) : null}

      {activeTab === "hours" ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <SectionTitle
            title="Horários de funcionamento"
            hint="Base usada pela agenda e pelo site público de agendamento."
          />
          <div className="space-y-2">
            {workingHours.map((item) => (
              <div
                key={item.weekday}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
              >
                <label className="flex w-32 shrink-0 items-center gap-2 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-pink-600 focus:ring-pink-500"
                    checked={item.isOpen}
                    onChange={(event) =>
                      handleWorkingHourChange(
                        item.weekday,
                        "isOpen",
                        event.target.checked
                      )
                    }
                  />
                  {WEEKDAY_LABELS[item.weekday]}
                </label>
                <input
                  type="time"
                  className="rounded-lg border border-slate-200 bg-white text-slate-900 transition placeholder:text-slate-400 focus:border-pink-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 px-2 py-1 text-sm"
                  value={item.startHour}
                  disabled={!item.isOpen}
                  onChange={(event) =>
                    handleWorkingHourChange(
                      item.weekday,
                      "startHour",
                      event.target.value
                    )
                  }
                />
                <span className="text-xs text-slate-400">até</span>
                <input
                  type="time"
                  className="rounded-lg border border-slate-200 bg-white text-slate-900 transition placeholder:text-slate-400 focus:border-pink-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 px-2 py-1 text-sm"
                  value={item.endHour}
                  disabled={!item.isOpen}
                  onChange={(event) =>
                    handleWorkingHourChange(
                      item.weekday,
                      "endHour",
                      event.target.value
                    )
                  }
                />
                {!item.isOpen ? (
                  <span className="text-xs text-slate-400">Fechado</span>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {activeTab === "site" ? (
        <section className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <SectionTitle
              title="Site público"
              hint="Página de agendamento online da empresa."
            />
            <div className="space-y-4">
              <Toggle
                name="site.isVisibleWebsite"
                register={register}
                label="Site visível"
                hint="Controla se a página pública fica acessível."
              />
              <Toggle
                name="site.isOnlineCalendar"
                register={register}
                label="Agendamento online habilitado"
                hint="Permite que o cliente final marque horário pelo site."
              />
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  type="text"
                  label="URL do site (slug)"
                  name="site.urlSite"
                  register={register}
                />
                <SelectInput
                  label="Tema de cor"
                  name="site.colorTheme"
                  register={register}
                >
                  {COLOR_THEMES.map((theme) => (
                    <option key={theme.value} value={theme.value}>
                      {theme.label}
                    </option>
                  ))}
                </SelectInput>
                <Input
                  type="text"
                  label="Endereço"
                  name="site.address"
                  register={register}
                />
                <PhoneInput
                  label="WhatsApp do site"
                  name="site.whatsapp"
                  register={register}
                />
              </div>
              <TextAreaInput
                label="Descrição"
                name="site.description"
                register={register}
                rows={3}
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <SectionTitle title="Redes sociais" />
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                type="text"
                label="Instagram"
                name="site.instagram"
                register={register}
              />
              <Input
                type="text"
                label="Facebook"
                name="site.facebook"
                register={register}
              />
              <Input
                type="text"
                label="TikTok"
                name="site.tiktok"
                register={register}
              />
              <Input
                type="text"
                label="Twitter / X"
                name="site.twitter"
                register={register}
              />
            </div>
          </div>
        </section>
      ) : null}

      {activeTab === "payments" ? (
        <section className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <SectionTitle
              title="Formas de pagamento"
              hint="Percentual descontado do valor da comanda em cada método."
            />
            <div className="space-y-2">
              {payments.map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">
                      {payment.name}
                    </p>
                    {payment.description ? (
                      <p className="truncate text-xs text-slate-500">
                        {payment.description}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      className="w-24 rounded-lg border border-slate-200 bg-white text-slate-900 transition placeholder:text-slate-400 focus:border-pink-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400 px-2 py-1 text-right text-sm"
                      value={payment.taxPercentage}
                      onChange={(event) =>
                        handlePaymentTaxChange(payment.id, event.target.value)
                      }
                    />
                    <span className="text-sm text-slate-500">%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <MachineList
            title="Máquinas de cartão de crédito"
            hint="Cada máquina tem a própria taxa por número de parcelas."
            machines={creditMachines}
            professionals={professionals}
            supportsInstallments
            onAdd={() =>
              setCreditMachines((previous) => [...previous, emptyMachine()])
            }
            onRemove={(index) =>
              setCreditMachines((previous) =>
                previous.filter((_, machineIndex) => machineIndex !== index)
              )
            }
            onChange={updateCreditMachine}
            onInstallmentRateChange={handleInstallmentRateChange}
            onToggleProfessional={toggleFeeBearerProfessional(setCreditMachines)}
          />

          <MachineList
            title="Máquinas de cartão de débito"
            hint="Débito é sempre à vista, com uma taxa única por máquina."
            machines={debitMachines}
            professionals={professionals}
            onAdd={() =>
              setDebitMachines((previous) => [
                ...previous,
                { ...emptyMachine(), installmentRates: [] },
              ])
            }
            onRemove={(index) =>
              setDebitMachines((previous) =>
                previous.filter((_, machineIndex) => machineIndex !== index)
              )
            }
            onChange={updateDebitMachine}
            onToggleProfessional={toggleFeeBearerProfessional(setDebitMachines)}
          />
        </section>
      ) : null}

      <FormFooter
        isSubmitting={isSubmitting}
        label={isEdit ? "Salvar alterações" : "Criar empresa"}
        hint={isEdit ? "Todas as abas são salvas de uma vez." : undefined}
        error={error}
      />
    </form>
  );
};

export default CompanySupportForm;
